import { useMemo } from 'react';
import { useAuthToken } from './useAuthToken';

const PROMETHEE_ADMIN = 'Promethee:Admin';
const PROMETHEE_UPDATE = 'Promethee:Update';
const PROMETHEE_READ = 'Promethee:Read';

// Nom de ce microservice dans le claim generique `backend_groups` du token (BO).
const SERVICE_BACKEND_NAME = 'promethee';

const EMPTY_PERMISSIONS = Object.freeze({
  isAuthenticated: false,
  isAccessTokenPayloadLoading: false,
  isStaff: false,
  isSuperuser: false,
  groups: [],
  isPrometheeAdmin: false,
  isPrometheeGlobalUpdate: false,
  isPrometheeGlobalRead: false,
  can: () => false,
});

/**
 * Hook RBAC cote front. Reflete a l'identique l'algo de PrometheeResourcePermission
 * (cf. hoortrade-promethee/modules/utils/permissions.py).
 *
 * On lit les claims directement depuis le payload de l'idToken (deja decode par
 * react-oidc via useAuthToken) : pas besoin de jwt-decode.
 *
 * Usage :
 *   const { isPrometheeAdmin, can } = useUserPermissions();
 *   if (can('update', 'Category')) { ... }
 *   if (can('delete', 'Product'))  { ... }
 *
 * Rappel : c'est purement UX (masquer des boutons). La securite reste backend.
 */
export const useUserPermissions = () => {
  const { accessTokenPayload, isAuthenticated } = useAuthToken();
  const isAccessTokenPayloadLoading = isAuthenticated && !accessTokenPayload;

  return useMemo(() => {
    if (!isAuthenticated || !accessTokenPayload) {
      return {
        ...EMPTY_PERMISSIONS,
        isAccessTokenPayloadLoading,
      };
    }

    const payload = accessTokenPayload;

    // Le BO expose un claim generique `backend_groups` : { promethee: [...], ... }.
    // On ne lit que le bucket de ce microservice.
    const backendGroups = payload.backend_groups?.[SERVICE_BACKEND_NAME];
    const groups = Array.isArray(backendGroups) ? backendGroups : [];
    const groupSet = new Set(groups);

    const isSuperuser = payload.is_superuser === true;
    const isPrometheeAdmin = isSuperuser || groupSet.has(PROMETHEE_ADMIN);
    const isPrometheeGlobalUpdate = groupSet.has(PROMETHEE_UPDATE);
    const isPrometheeGlobalRead = groupSet.has(PROMETHEE_READ);

    /**
     * @param {'read'|'create'|'update'|'delete'} action
     * @param {string} resource - nom du modele Django, ex: 'Category'
     */
    const can = (action, resource) => {
      if (isPrometheeAdmin) return true;
      const key = (a) => `Promethee:${a}:${resource}`;
      switch (action) {
        case 'read':
          if (isPrometheeGlobalRead || isPrometheeGlobalUpdate) return true;
          return (
            groupSet.has(key('Read')) ||
            groupSet.has(key('Create')) ||
            groupSet.has(key('Update')) ||
            groupSet.has(key('Delete'))
          );
        case 'create':
          return isPrometheeGlobalUpdate || groupSet.has(key('Create'));
        case 'update':
          return isPrometheeGlobalUpdate || groupSet.has(key('Update'));
        case 'delete':
          // pas de raccourci global pour Delete (cf. section 1.2 du guide)
          return groupSet.has(key('Delete'));
        default:
          return false;
      }
    };

    return {
      isAuthenticated: true,
      isAccessTokenPayloadLoading: false,
      isStaff: payload.is_staff === true,
      isSuperuser,
      groups,
      isPrometheeAdmin,
      isPrometheeGlobalUpdate,
      isPrometheeGlobalRead,
      can,
    };
  }, [accessTokenPayload, isAuthenticated, isAccessTokenPayloadLoading]);
};
